import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { settingsService } from '../../services/settingsService';

const GeneralSettings: React.FC = () => {
  const [adminCommission, setAdminCommission] = useState<string>('');
  const [washerCharges, setWasherCharges] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const commission = await settingsService.getAdminCommission();
        setAdminCommission(commission.toString());

        const washer = await settingsService.getWasherCharges();
        setWasherCharges(washer.toString());
      } catch (err) {
        console.error('Error loading settings:', err);
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const commissionValue = parseFloat(adminCommission);
      if (isNaN(commissionValue) || commissionValue < 0 || commissionValue > 100) {
        throw new Error('Please enter a valid commission between 0 and 100');
      }

      await settingsService.updateAdminCommission(commissionValue);

      const washerValue = parseFloat(washerCharges);
      if (isNaN(washerValue) || washerValue < 0) {
        throw new Error('Please enter a valid washer charges value');
      }

      await settingsService.updateWasherCharges(washerValue);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={0}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 0, }}>
      
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={0}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Admin Commission Rate
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Set the commission percentage that will be applied to all transactions.
            </Typography>
            
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <TextField
                type="number"
                size="small"
                value={adminCommission}
                onChange={(e) => setAdminCommission(e.target.value)}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 0.1
                }}
                sx={{ width: 150 }}
                InputProps={{
                  endAdornment: '%',
                }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : null}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6} sx={{ mt: { xs: 3, md: 0 } }}>
            <Typography variant="subtitle1" gutterBottom>
              Washer Charges
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Configure the default charges applied for washers.
            </Typography>

            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <TextField
                type="number"
                size="small"
                value={washerCharges}
                onChange={(e) => setWasherCharges(e.target.value)}
                InputProps={{
                  endAdornment: '%',
                }}
                sx={{ width: 150 }}
              />
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : null}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default GeneralSettings;
